import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export function useSerialNumbers(options: {
  item_id?: string;
  location_id?: string;
  status?: string;
  search?: string;
  page?: number;
  page_size?: number;
} = {}) {
  return useQuery({
    queryKey: ['serial-numbers', options],
    queryFn: () => api.getSerialNumbers(options),
    staleTime: 5 * 60 * 1000,
  });
}

export function useSerialNumber(serialId: string) {
  return useQuery({
    queryKey: ['serial-numbers', serialId],
    queryFn: () => api.getSerialNumber(serialId),
    enabled: !!serialId,
  });
}

export function useSerialTransactions(serialId: string) {
  return useQuery({
    queryKey: ['serial-transactions', serialId],
    queryFn: () => api.getSerialTransactions(serialId),
    enabled: !!serialId,
  });
}

export function useSerialStats() {
  return useQuery({
    queryKey: ['serial-stats'],
    queryFn: () => api.getSerialStats(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useExpiringWarranties(days: number = 30) {
  return useQuery({
    queryKey: ['expiring-warranties', days],
    queryFn: () => api.getExpiringWarranties(days),
  });
}

export function useCreateSerial() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => api.createSerialNumber(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['serial-numbers'] });
      queryClient.invalidateQueries({ queryKey: ['serial-stats'] });
    },
  });
}

export function useUpdateSerial() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => 
      api.updateSerialNumber(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['serial-numbers'] });
    },
  });
}

export function useTransferSerial() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ serialId, toLocationId, notes }: { 
      serialId: string; 
      toLocationId: string; 
      notes?: string;
    }) => api.transferSerialNumber(serialId, toLocationId, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['serial-numbers'] });
      queryClient.invalidateQueries({ queryKey: ['serial-transactions'] });
    },
  });
}

export function useAvailableSerialsForItem(itemId: string) {
  return useQuery({
    queryKey: ['available-serials', itemId],
    queryFn: () => api.getAvailableSerialsForItem(itemId),
    enabled: !!itemId,
  });
}
